/**
 * QuickFadePresence — fondu superposé (crossfade) à l'apparition / disparition.
 */

import {
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import styles from './quick-fade-presence.module.css';

export const QUICK_UI_FADE_MS = 180;

export type UiFade = 'in' | 'out';

interface QuickFadePresenceProps {
  show: boolean;
  contentKey?: string;
  children: ReactNode;
  className?: string;
}

interface FadeLayer {
  key: string;
  node: ReactNode;
}

function applyUiFade(node: ReactNode, uiFade: UiFade): ReactNode {
  if (Array.isArray(node)) {
    return node.map((child, index) => {
      const faded = applyUiFade(child, uiFade);
      if (isValidElement(faded) && faded.key == null) {
        return cloneElement(faded, { key: index });
      }
      return faded;
    });
  }
  if (isValidElement(node)) {
    const extras: { uiFade: UiFade; keyboardActive?: boolean } = { uiFade };
    if (uiFade === 'out') extras.keyboardActive = false;
    return cloneElement(node as ReactElement<{ uiFade?: UiFade; keyboardActive?: boolean }>, extras);
  }
  return node;
}

export function QuickFadePresence({
  show,
  contentKey = 'default',
  children,
  className,
}: QuickFadePresenceProps) {
  const activeKey = show ? contentKey : null;
  const prevKeyRef = useRef<string | null>(activeKey);
  const prevChildrenRef = useRef<ReactNode>(children);
  const [outgoing, setOutgoing] = useState<FadeLayer | null>(null);

  useLayoutEffect(() => {
    if (activeKey !== prevKeyRef.current) {
      if (prevKeyRef.current !== null) {
        setOutgoing({ key: prevKeyRef.current, node: prevChildrenRef.current });
      }
      prevKeyRef.current = activeKey;
    }
    prevChildrenRef.current = children;
  }, [activeKey, children]);

  useEffect(() => {
    if (!outgoing) return undefined;
    const timer = window.setTimeout(() => setOutgoing(null), QUICK_UI_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [outgoing]);

  if (!activeKey && !outgoing) return null;

  return (
    <div className={[styles.stack, className].filter(Boolean).join(' ')}>
      {outgoing && (
        <div className={styles.layer} aria-hidden>
          {applyUiFade(outgoing.node, 'out')}
        </div>
      )}
      {activeKey && (
        <div className={styles.layer}>
          {applyUiFade(children, 'in')}
        </div>
      )}
    </div>
  );
}
